import { ChevronDown, ChevronUp, FileText, PenLine, Plus } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { TranscriptEditor } from "@/components/ui/visits/TranscriptEditor";
import {
  TranscriptFlagsCount,
  TranscriptFlagsLegend,
  TranscriptFlagsProvider,
} from "@/components/ui/visits/TranscriptFlags";
import { TranscriptionsCard } from "@/components/ui/visits/transcriptions-card";
import { reviewTextKey } from "@/hooks/visits/useVisitReview";
import {
  addRecordingBlock,
  addRecordingBlockMessage,
} from "@/lib/add-recording";
import { cn } from "@/lib/utils";
import type { VisitRecording } from "@/lib/visit-content-queries";
import type { FileData } from "@/services/files";

export const transcriptPreview = (
  transcription: string | null | undefined,
  length = 140,
): string => {
  const text = (transcription ?? "").replace(/\s+/g, " ").trim();
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
};

interface Props {
  file: FileData | null;
  multiRecordingFiles: VisitRecording[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  // Shown when multi-recording is enabled for this environment.
  onAddRecording?: () => void;
  // Phones: a slim bar docked at the bottom of the screen that opens the
  // transcript in a bottom sheet. From 640px the bar sits under the note.
  docked?: boolean;
  // The first recording's transcript can be corrected now (latest version,
  // nothing processing, VITE_TRANSCRIPT_EDIT_ENABLED).
  canEdit?: boolean;
  // A corrected transcript was saved.
  onTranscriptSaved?: (change: { before: string; after: string }) => void;
}

// The transcript, collapsed by default into a slim bar under the note so the
// note keeps the screen. Expanding shows every recording of the visit.
// AI flags on the transcript (VITE_TRANSCRIPT_FLAGS_ENABLED) wrap the bar so
// its count and the marks in the text share one read.
export const TranscriptBar = (props: Props): JSX.Element => {
  const { file, multiRecordingFiles } = props;
  const revision = [
    file?.status,
    reviewTextKey(file?.transcription),
    reviewTextKey(file?.result),
    ...multiRecordingFiles.map(
      (recording) =>
        `${recording.status}:${reviewTextKey(recording.transcription)}`,
    ),
  ].join("|");
  return (
    <TranscriptFlagsProvider
      enabled={AppConfig.enableTranscriptFlags && !!file}
      revision={revision}
      visitPublicId={file?.public_id}
    >
      <TranscriptBarInner {...props} />
    </TranscriptFlagsProvider>
  );
};

const TranscriptBarInner = ({
  file,
  multiRecordingFiles,
  expanded,
  onExpandedChange,
  expandedItems,
  toggleExpanded,
  onAddRecording,
  docked = false,
  canEdit = false,
  onTranscriptSaved,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const contentId = useId();
  const recordings = (file ? 1 : 0) + multiRecordingFiles.length;
  const preview = transcriptPreview(file?.transcription);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const isEditing = editing && canEdit && !!file;

  const setExpanded = (next: boolean): void => {
    // Unsaved corrections: ask before the transcript closes.
    if (!next && isEditing && dirty) {
      setAskDiscard(true);
      return;
    }
    if (!next) setEditing(false);
    // Open the first recording too, so expanding shows text straight away.
    if (next && file && expandedItems.length === 0)
      toggleExpanded(file.public_id);
    onExpandedChange(next);
  };
  const stopEditing = useCallback(() => {
    setEditing(false);
    setAskDiscard(false);
    setDirty(false);
  }, []);
  const editButton = canEdit && file && !isEditing && (
    <Button
      className="h-11 shrink-0 lg:h-9"
      data-testid="transcript-edit"
      type="button"
      variant="ghost"
      onClick={() => {
        if (!expanded) onExpandedChange(true);
        setEditing(true);
      }}
    >
      <PenLine />
      <span className="max-[399px]:sr-only">
        {t("visit.transcriptEdit.edit")}
      </span>
    </Button>
  );
  const body =
    isEditing && file ? (
      <TranscriptEditor
        askDiscard={askDiscard}
        text={file.transcription ?? ""}
        visitPublicId={file.public_id}
        onAskDiscardChange={setAskDiscard}
        onCancel={stopEditing}
        onDirtyChange={setDirty}
        onSaved={(change) => {
          stopEditing();
          onTranscriptSaved?.(change);
          // Phones: close the sheet so the "Update note" bar is in view.
          if (docked) onExpandedChange(false);
        }}
      />
    ) : (
      <>
        <TranscriptFlagsLegend className="pt-3" />
        <TranscriptionsCard
          embedded
          expandedItems={expandedItems}
          file={file}
          multiRecordingFiles={multiRecordingFiles}
          toggleExpanded={toggleExpanded}
        />
      </>
    );

  // Adding a recording while the visit or a recording is still processing
  // fails that job and leaves the visit in Error, so wait until it settles.
  const blocked = addRecordingBlockMessage(
    addRecordingBlock(file, multiRecordingFiles),
    t,
  );
  const addRecording = onAddRecording && file && (
    <SubscriptionOnlyButton
      className="size-11 px-0 lg:size-9"
      disabledReason={blocked}
      validations={[
        ValidationSuscriptionButton.PLAN_FREE_TRIAL,
        ValidationSuscriptionButton.RECORDING_USAGES,
      ]}
      variant="ghost"
      onClick={onAddRecording}
    >
      <Plus />
      <span className="sr-only">
        {t("visit.workspace.addRecording")}
        {blocked && `. ${blocked}`}
      </span>
    </SubscriptionOnlyButton>
  );
  const count = (className?: string) =>
    recordings > 0 && (
      <span
        className={cn(
          "shrink-0 whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground",
          className,
        )}
      >
        {t("visit.workspace.recordings", { count: recordings })}
      </span>
    );

  if (docked) {
    // Left of the Feedback button (44px round, 12px from the corner on
    // phones) and above the home indicator; the page already keeps 72px
    // clear at the bottom. It sits under every dialog and sheet (z-40 < z-50).
    return (
      <>
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pl-3 pr-16"
          data-state={expanded ? "expanded" : "collapsed"}
          data-testid="transcript-dock"
        >
          <button
            aria-controls={contentId}
            aria-expanded={expanded}
            aria-haspopup="dialog"
            className="pointer-events-auto flex h-11 w-full items-center gap-2 rounded-full border bg-card/95 pl-4 pr-3 text-left shadow-[0_6px_24px_-8px_rgba(0,0,0,0.35)] backdrop-blur-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            type="button"
            onClick={() => {
              setExpanded(true);
            }}
          >
            <FileText
              aria-hidden
              className="size-4 shrink-0 text-muted-foreground"
            />
            <span className="shrink-0 text-sm font-semibold">
              {t("visit.workspace.transcript")}
            </span>
            {recordings > 1 && count()}
            <TranscriptFlagsCount />
            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
              {preview || t("visit.workspace.transcriptPending")}
            </span>
            <ChevronUp
              aria-hidden
              className="size-4 shrink-0 text-muted-foreground"
            />
          </button>
        </div>
        <Sheet open={expanded} onOpenChange={setExpanded}>
          <SheetContent
            aria-describedby={undefined}
            className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl p-0 [&_button.absolute]:w-11!"
            data-testid="transcript-sheet"
            id={contentId}
            side="bottom"
          >
            <div
              aria-hidden
              className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
            />
            <div className="flex items-center gap-2 border-b py-2 pl-4 pr-14">
              <SheetTitle className="text-[17px] font-semibold leading-[22px]">
                {t("visit.workspace.transcript")}
              </SheetTitle>
              {count()}
              <div className="ml-auto flex items-center gap-1">
                {editButton}
                {!isEditing && addRecording}
              </div>
            </div>
            {addRecording && blocked && (
              <p
                className="border-b px-4 py-2 text-xs text-muted-foreground"
                data-testid="add-recording-blocked"
              >
                {blocked}
              </p>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[env(safe-area-inset-bottom,0px)] [&_button]:min-h-11">
              {body}
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <section
      aria-label={t("visit.workspace.transcript")}
      className={cn(
        "rounded-xl border bg-card",
        !expanded && "lg:shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)]",
      )}
      data-state={expanded ? "expanded" : "collapsed"}
      data-testid="transcript-bar"
    >
      <div className="flex items-center gap-3 px-4 py-2 sm:px-5">
        <FileText
          aria-hidden
          className="size-5 shrink-0 text-muted-foreground"
        />
        <div className="flex min-w-0 flex-1 items-center gap-x-3 overflow-hidden">
          <h2 className="shrink-0 text-[17px] font-semibold leading-[22px]">
            {t("visit.workspace.transcript")}
          </h2>
          {count("max-[399px]:hidden lg:max-xl:hidden")}
          <TranscriptFlagsCount />
          {!expanded && (
            <p className="hidden min-w-0 flex-1 truncate text-sm text-muted-foreground xl:block">
              {preview || t("visit.workspace.transcriptPending")}
            </p>
          )}
        </div>
        {expanded && editButton}
        {!isEditing && addRecording}
        <Button
          aria-controls={contentId}
          aria-expanded={expanded}
          className="h-11 shrink-0 lg:h-9"
          type="button"
          variant="outline"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded ? <ChevronDown /> : <ChevronUp />}
          {t(
            expanded
              ? "visit.workspace.collapseTranscript"
              : "visit.workspace.expandTranscript",
          )}
        </Button>
      </div>
      {expanded && (
        <div
          className="max-h-[60dvh] overflow-y-auto border-t px-4 sm:px-5 [&_button]:min-h-11 lg:[&_button]:min-h-0"
          id={contentId}
        >
          {body}
        </div>
      )}
    </section>
  );
};
